import sortBy from 'lodash/sortBy';
import { atom, DefaultValue, selector } from 'recoil';
import { ProductTaxonomyType } from '../../@sprinx/knihovka-types';
import { ApiClient } from '../../@sprinx/react-after-razzle';
import { GlobalStateRegister } from '../../@sprinx/react-after-razzle/stateStore';
import { apiClientState } from '../appState';

const LOYALTY_PROGRAM_TAXONOMY_ROOT = '/loyaltyProgram';

export const getProductTaxonomyLoyaltyProgramTypes = (apiClient: ApiClient): Promise<ProductTaxonomyType[]> => {
  return apiClient.get<ProductTaxonomyType[], {}>('/v1/product-taxonomy-types-loyalty-program');
};

export const productTaxonomyLoyaltyProgramTypesInitialState = GlobalStateRegister.register(
  atom<ProductTaxonomyType[] | undefined>({
    key: 'productTaxonomyLoyaltyProgramTypesInitial',
    default: undefined,
  }),
);

export const productTaxonomyLoyaltyProgramTypesReloadTrigger = atom<number>({
  key: 'productTaxonomyLoyaltyProgramTypesReloadTrigger',
  default: 1,
});

export const productTaxonomyLoyaltyProgramTypesQuery = selector<ProductTaxonomyType[]>({
  key: 'productTaxonomyLoyaltyProgramTypes',
  get: ({ get }) => {
    get(productTaxonomyLoyaltyProgramTypesReloadTrigger);
    const initial = get(productTaxonomyLoyaltyProgramTypesInitialState);
    if (initial !== undefined) return initial;

    const apiClient = get(apiClientState);
    return getProductTaxonomyLoyaltyProgramTypes(apiClient);
  },
  set: ({ set }, newValue) => {
    if (newValue instanceof DefaultValue) {
      set(productTaxonomyLoyaltyProgramTypesReloadTrigger, (n) => n + 1);
    }
  },
});

export const catalogueTaxonomyLoyaltyProgramTypesSelector = selector<ProductTaxonomyType[]>({
  key: 'catalogueTaxonomyLoyaltyProgramTypes',
  get: ({ get }) => {
    const allTaxonomies = get(productTaxonomyLoyaltyProgramTypesQuery);

    return sortBy(
      allTaxonomies.filter(
        (tx) => tx.code.startsWith(`${LOYALTY_PROGRAM_TAXONOMY_ROOT}/`) || tx.code === LOYALTY_PROGRAM_TAXONOMY_ROOT,
      ),
      [(i) => i.extra.position],
    );
  },
});

export const catalogueTaxonomyLoyaltyProgramTypesLeveledSelector = selector<{
  items: ProductTaxonomyType[];
  maxLevel: number;
}>({
  key: 'catalogueTaxonomyLoyaltyProgramTypesLeveled',
  get: ({ get }) => {
    const catalogueTaxonomies = get(catalogueTaxonomyLoyaltyProgramTypesSelector);
    const maxLevel = catalogueTaxonomies
      .map((i) => (i.code.match(/\//g) || []).length)
      .reduce((max, i) => (max < i ? i : max), 0);

    return {
      items: catalogueTaxonomies,
      maxLevel,
    };
  },
});

export const catalogueTaxonomiesHierarchyLoyaltyProgramSelector = selector<ProductTaxonomyTypeHierarchyNode>({
  key: 'catalogueTaxonomiesHierarchyLoyaltyProgramSelector',
  get: ({ get }) => {
    const catalogueTaxonomiesLeveled = get(catalogueTaxonomyLoyaltyProgramTypesLeveledSelector);

    const traverse = (node: ProductTaxonomyType, items: ProductTaxonomyType[]): ProductTaxonomyTypeHierarchyNode => {
      const rx = new RegExp(`^${node.code}/[^/]+$`, 'i');
      const children = items.filter((i) => rx.test(i.code)).map((prt) => traverse(prt, items));

      return children.length > 0 ? { ...node, children } : node;
    };

    let rootTaxonomy = catalogueTaxonomiesLeveled.items.find((n) => n.code === LOYALTY_PROGRAM_TAXONOMY_ROOT);

    if (!rootTaxonomy) {
      rootTaxonomy = {
        code: LOYALTY_PROGRAM_TAXONOMY_ROOT,
        extra: {},
        image: '',
        label: [
          {
            language: 'cs',
            text: 'Věrnostní program',
          },
          {
            language: 'sk',
            text: 'Vernostný program',
          },
        ],
      };
    }

    const root = traverse(rootTaxonomy, catalogueTaxonomiesLeveled.items);

    return root;
  },
});

export interface ProductTaxonomyTypeHierarchyNode extends ProductTaxonomyType {
  children?: ProductTaxonomyTypeHierarchyNode[];
}
